/* eslint-disable sonarjs/no-duplicate-string */
import type { StorybookConfig } from '@storybook/react-vite'

// Questi id numerici arrivano da netsuite e sono usati per identificare le stagioni dei prodotti
export const seasonsMap = {
  SPRING_SUMMER: 1,
  ALL_SEASONS: 2,
  FALL_WINTER: 3,
  THREE_SEASONS: 4,
} as const

export const notifyMeSeasonSummerStartDate = '2017-01-01'
export const notifyMeSeasonSummerEndDate = '2017-06-30'
export const notifyMeSeasonWinterStartDate = '2017-07-01'
export const notifyMeSeasonWinterEndDate = '2017-12-31'

export const instagramCardsAmount = 12

export const fakePixelImage =
  'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=='

export const addToCartStatusChangeMilliseconds = 2500

export /**
 * 9/16 = 0.5625.
 */
const portraitAspectRatio = 0.5625

export const promotionalBannerVariants = {
  PRIMARY: 'primary',
  SEMI_TRANSPARENT: 'semiTransparent',
} as const

export const defaultBottegaDetails = {
  title: '',
  subtitle: '',
  form_title: '',
  form_placeholder: '',
  pretitle: '',
  title_02: '',
  paragraph: '',
  label_read_more: '',
  hours_html: '',
  label_hours: '',
  label_mon_fri: '',
  label_sat: '',
  label_sun: '',
  label_address: '',
  label_nearest_bottega: '',
  cta_gmaps_page_link: '',
} as const

export const defaultCityIcons = {
  title: '',
  subtitle: '',
  form_title: '',
  form_placeholder: '',
  pretitle: '',
  title_02: '',
  paragraph: '',
  label_read_more: '',
  hours_html: '',
  label_hours: '',
  label_mon_fri: '',
  label_sat: '',
  label_sun: '',
  label_address: '',
} as const

export const defaultSettings = {
  language: 'it',
  collection_commons_sole_in: 'Suola in',
  cross_currency: '€',
  currency: 'EUR',
  reviews_label: 'Recensioni',
  store_nation: 'it',
  ns_lang_value: '1',
  locale: 'it-IT',
  cart_delete: 'Rimuovi',
  imageAssetPublicPath: '//velasca-beta.myshopify.com/cdn/shop/files/',
  currency_format: 'money_with_currency_format',
  money_format: '€ {{amount}}',
  money_with_currency_format: 'EUR {{amount}}',
} as const

export const SHOPIFY_API_VERSION = '2025-07'

export const betaGraphqlSchemaUri = `https://velasca-beta.myshopify.com/api/${SHOPIFY_API_VERSION}/graphql.json`

/** @public */
export const storybookConfigBase: StorybookConfig = {
  stories: ['../src/**/*.stories.@(js|jsx|ts|tsx)'],
  addons: ['@storybook/addon-links', '@storybook/addon-vitest'],
  framework: {
    name: '@storybook/react-vite',
    options: {},
  },
  staticDirs: ['./theme_assets'],
}

export const brands = ['candiani', 'attanasio'] as const

interface BrandTheme {
  colors: {
    primary: string
    secondary: string
    primaryLight: string
    hover: string
    background: string
    boxBackground: string
    modalBackground: string
    backgroundOverlay: string
    disabled: string
    sliderBackground: string
    carouselBackground: string
    sliderActionButtonActive: string
    sliderActionButtonInactive: string
    footerBackground: string
    danger: string
    brandFrame: string
    border: string
    footerSeparator: string
    modalShadow: string
    skeleton: {
      baseColor: string
      highlightColor: string
    }
    text: {
      primary: string
      light: string
      secondary: string
      military500?: string
    }
  }
}

export const colorsPalette = {
  transparent: 'rgba(0, 0, 0, 0)',
  semiTransparent: 'rgba(49, 51, 50, 0.6);',
  darkTransparent: '#000000a6',
  pureWhite: '#FFFFFF',
  light: '#FFFEFA',
  dark: '#313332',
  brownSugar500: '#B0684F',
  bole500: '#854E41',
  metal500: '#8B959A',
  metal400: '#C2C3C3',
  metal300: '#D9D9D9',
  metal200: '#EEEEEE',
  metal100: '#F9F8F7',
  ocean500: '#185674',
  ocean400: '#427892',
  // ocean300: '#7691A3', unused
  emerald500: '#23595B',
  emerald400: '#457B7D',
  // emerald200: '#DAE0E0', unused
  sand500: '#A39062',
  sand400: '#CABC9B',
  sand300: '#DBD4C3',
  sand200: '#EAE5DC',
  sand100: '#F3EFE7',
  navy500: '#385181',
  navy300: '#6C7EA1',
  // navy200: '#DEE3EC', unused
  cloud500: '#8596B0',
  cloud400: '#A5B1C3',
  cloud300: '#C2C8D1',
  cloud200: '#E2E5EA',
  cloud100: '#F3F4F4',
  // indigo500: '#242959', unused
  // indigo300: '#767994', unused
  // indigo200: '#D1D1D9', unused
  // mud500: '#AD8E71', unused
  // mud400: '#C9B29D', unused
  // mud300: '#D9CCC0', unused
  // mud200: '#EEE4DB', unused
  // mud100: '#F6F4F1', unused
  military500: '#4B6C5E',
  military400: '#628173',
  military300: '#BAC3B8',
  military100: '#F8F6F0',
}

const velascaManTheme: BrandTheme = {
  colors: {
    primary: colorsPalette.ocean500,
    secondary: colorsPalette.metal300,
    primaryLight: colorsPalette.ocean400,
    hover: colorsPalette.metal500,
    background: colorsPalette.pureWhite,
    boxBackground: colorsPalette.metal100,
    modalBackground: colorsPalette.metal200,
    backgroundOverlay: colorsPalette.semiTransparent,
    sliderBackground: colorsPalette.metal100,
    sliderActionButtonActive: colorsPalette.metal500,
    sliderActionButtonInactive: colorsPalette.metal300,
    carouselBackground: colorsPalette.metal200,
    footerBackground: colorsPalette.dark,
    disabled: colorsPalette.metal400,
    danger: colorsPalette.brownSugar500,
    border: colorsPalette.metal400,
    brandFrame: colorsPalette.ocean500,
    footerSeparator: colorsPalette.metal500,
    modalShadow: colorsPalette.darkTransparent,
    skeleton: {
      baseColor: colorsPalette.metal300,
      highlightColor: colorsPalette.metal200,
    },
    text: {
      primary: colorsPalette.dark,
      secondary: colorsPalette.pureWhite,
      light: colorsPalette.metal500,
      military500: colorsPalette.military500,
    },
  },
}

const velascaWomanTheme: BrandTheme = {
  colors: {
    primary: colorsPalette.military500,
    secondary: colorsPalette.metal300,
    primaryLight: colorsPalette.military400,
    hover: colorsPalette.metal500,
    background: colorsPalette.pureWhite,
    boxBackground: colorsPalette.military100,
    modalBackground: colorsPalette.metal100,
    backgroundOverlay: colorsPalette.semiTransparent,
    sliderBackground: colorsPalette.military100,
    sliderActionButtonActive: colorsPalette.military500,
    sliderActionButtonInactive: colorsPalette.military300,
    carouselBackground: colorsPalette.metal100,
    footerBackground: colorsPalette.military500,
    disabled: colorsPalette.metal400,
    danger: colorsPalette.bole500,
    border: colorsPalette.metal400,
    brandFrame: colorsPalette.ocean500,
    footerSeparator: colorsPalette.military300,
    modalShadow: colorsPalette.darkTransparent,
    skeleton: {
      baseColor: colorsPalette.metal300,
      highlightColor: colorsPalette.metal200,
    },
    text: {
      primary: colorsPalette.dark,
      secondary: colorsPalette.pureWhite,
      light: colorsPalette.metal500,
    },
  },
}

const attanasioTheme: BrandTheme = {
  colors: {
    primary: colorsPalette.emerald500,
    secondary: colorsPalette.sand300,
    primaryLight: colorsPalette.emerald400,
    hover: colorsPalette.sand500,
    background: colorsPalette.pureWhite,
    boxBackground: colorsPalette.sand100,
    modalBackground: colorsPalette.sand200,
    backgroundOverlay: colorsPalette.semiTransparent,
    sliderBackground: colorsPalette.pureWhite,
    carouselBackground: colorsPalette.transparent,
    sliderActionButtonActive: colorsPalette.military500,
    sliderActionButtonInactive: colorsPalette.military300,
    footerBackground: colorsPalette.dark,
    disabled: colorsPalette.sand400,
    danger: colorsPalette.brownSugar500,
    brandFrame: colorsPalette.sand400,
    border: colorsPalette.sand400,
    footerSeparator: colorsPalette.metal500,
    modalShadow: colorsPalette.darkTransparent,
    skeleton: {
      baseColor: colorsPalette.sand300,
      highlightColor: colorsPalette.sand200,
    },
    text: {
      primary: colorsPalette.dark,
      light: colorsPalette.sand200,
      secondary: colorsPalette.light,
    },
  },
}

const candianiTheme: BrandTheme = {
  colors: {
    primary: colorsPalette.navy500,
    secondary: colorsPalette.cloud300,
    primaryLight: colorsPalette.navy300,
    hover: colorsPalette.cloud500,
    background: colorsPalette.pureWhite,
    boxBackground: colorsPalette.cloud100,
    modalBackground: colorsPalette.cloud200,
    backgroundOverlay: colorsPalette.semiTransparent,
    sliderBackground: colorsPalette.cloud100,
    sliderActionButtonActive: colorsPalette.cloud500,
    sliderActionButtonInactive: colorsPalette.cloud300,
    carouselBackground: colorsPalette.cloud200,
    footerBackground: colorsPalette.dark,
    disabled: colorsPalette.cloud300,
    danger: colorsPalette.brownSugar500,
    border: colorsPalette.cloud400,
    brandFrame: colorsPalette.navy500,
    footerSeparator: colorsPalette.cloud500,
    modalShadow: colorsPalette.darkTransparent,
    skeleton: {
      baseColor: colorsPalette.cloud200,
      highlightColor: colorsPalette.cloud300,
    },
    text: {
      primary: colorsPalette.dark,
      secondary: colorsPalette.pureWhite,
      light: colorsPalette.cloud500,
    },
  },
}

export const designSystemTheme = {
  man: {
    ...velascaManTheme,
    logo: {
      primary: colorsPalette.ocean500,
      white: colorsPalette.pureWhite,
    },
    fonts: {
      brand: "'Jost', serif;",
      serif: {
        fontFamily: "'Jomolhari', serif;",
      },
    },
    brand: {
      velasca: velascaManTheme,
      attanasio: attanasioTheme,
      candiani: candianiTheme,
    },
  },
  woman: {
    ...velascaWomanTheme,
    logo: {
      primary: colorsPalette.military500,
      white: colorsPalette.pureWhite,
    },
    fonts: {
      brand: "'Jost', serif;",
      serif: {
        fontFamily: "'Jomolhari', serif;",
      },
    },
    brand: {
      velasca: velascaWomanTheme,
      attanasio: attanasioTheme,
      candiani: candianiTheme,
    },
  },
}

/** @public */
export const featureFlagsCatalog = {
  /**
   * Key of the PostHog feature flag.
   */
  COLLECTION_PAGE_CARDS: 'collection-page-indossate-experiment',
  /**
   * Key of the PostHog feature flag.
   */
  FILTERS: 'collection-page-filters-experiment',
} as const

export const dataTestIds = {
  SHOP_THE_LOOK_BUTTON: 'shop-the-look-button',
  ADD_TO_CART_TEXT: 'add-to-cart-text',
  MENU_LINKS: 'menu-links',
  PRODUCT_PRICE_IN_CARD: 'product-price-in-card',
  RECENT_PRODUCTS_SLIDER: 'recent-products-slider',
  COMPLEMENTARY_PRODUCTS_SLIDER: 'complementary-products-slider',
  SIMILAR_PRODUCTS_SLIDER: 'similar-products-slider',
  GENERIC_PRODUCTS_SLIDER: 'generic-products-slider',
  QUICK_ADD_TO_CART_BUTTON: 'quick-add-to-cart-button',
  CART_ICON: 'cart-icon',
  QUICK_ADD_TO_CART_LINE_ITEM: 'quick-add-to-cart-line-item',
  HOMEPAGE_HERO_CTA: 'homepage-hero-cta',
  MOBILE_SUGGESTED_PRODUCTS: 'mobile-suggested-products',
  CART_PRODUCT_LIST: 'cart-product-list',
  LINE_ITEM: 'line-item',
  SIZE_SELECTOR_ITEM: 'size-selector-item',
} as const

export const fallbackStore = 'row'

export const supportedIsocodesByStore = {
  it: ['IT', 'SM', 'VA'],
  de: ['DE'],
  eu: [
    'AT',
    'BE',
    'BG',
    'HR',
    'CY',
    'CZ',
    'EE',
    'FI',
    'GR',
    'HU',
    'IE',
    'LV',
    'LT',
    'LU',
    'MT',
    'NL',
    'PT',
    'RO',
    'SK',
    'SI',
    'ES',
    'SE',
    'PL',
  ],
  dk: ['DK'],
  uk: ['GB'],
  fr: ['FR', 'MC'],
  us: ['US'],
}

export const velascaApi = {
  production: {
    MAN: 'https://api.velasca.com',
    WOMAN: 'https://api.velascawomen.com',
  },
  staging: {
    MAN: 'https://sg.api.velasca.com',
    WOMAN: 'https://sg.api.velascawomen.com',
  },
} as const

export const allProductsCategoryTranslations = [
  'tutti-i-prodotti',
  'all-products',
  'tous-les-produits',
  'alle-produkte',
]

export const countryList = {
  AU: {
    code: 'AU',
    label: {
      EN: 'Australia',
      FR: 'Australie',
      IT: 'Australia',
      DE: 'Australien',
    },
  },
  AT: {
    code: 'AT',
    label: {
      EN: 'Austria',
      FR: 'Autriche',
      IT: 'Austria',
      DE: 'Österreich',
    },
  },
  BE: {
    code: 'BE',
    label: {
      EN: 'Belgium',
      FR: 'Belgique',
      IT: 'Belgio',
      DE: 'Belgien',
    },
  },
  CA: {
    code: 'CA',
    label: {
      EN: 'Canada',
      FR: 'Canada',
      IT: 'Canada',
      DE: 'Kanada',
    },
  },
  DK: {
    code: 'DK',
    label: {
      EN: 'Denmark',
      FR: 'Danemark',
      IT: 'Danimarca',
      DE: 'Dänemark',
    },
  },
  FR: {
    code: 'FR',
    label: {
      EN: 'France',
      FR: 'France',
      IT: 'Francia',
      DE: 'Frankreich',
    },
  },
  DE: {
    code: 'DE',
    label: {
      EN: 'Germany',
      FR: 'Allemagne',
      IT: 'Germania',
      DE: 'Deutschland',
    },
  },
  GR: {
    code: 'GR',
    label: {
      EN: 'Greece',
      FR: 'Gréce',
      IT: 'Grecia',
      DE: 'Griechenland',
    },
  },
  HK: {
    code: 'HK',
    label: {
      EN: 'Hong Kong',
      FR: 'Hong Kong',
      IT: 'Hong Kong',
      DE: 'Hongkong',
    },
  },
  IT: {
    code: 'IT',
    label: {
      EN: 'Italy',
      FR: 'Italie',
      IT: 'Italia',
      DE: 'Italien',
    },
  },
  LU: {
    code: 'LU',
    label: {
      EN: 'Luxembourg',
      FR: 'Luxembourg',
      IT: 'Lussemburgo',
      DE: 'Luxemburg',
    },
  },
  NL: {
    code: 'NL',
    label: {
      EN: 'Netherlands',
      FR: 'Pays-Bas',
      IT: 'Paesi Bassi',
      DE: 'Niederlande',
    },
  },
  NO: {
    code: 'NO',
    label: {
      EN: 'Norway',
      FR: 'Norvège',
      IT: 'Norvegia',
      DE: 'Norwegen',
    },
  },
  PT: {
    code: 'PT',
    label: {
      EN: 'Portugal',
      FR: 'Portugal',
      IT: 'Portogallo',
      DE: 'Portugal',
    },
  },
  SG: {
    code: 'SG',
    label: {
      EN: 'Singapore',
      FR: 'Singapour',
      IT: 'Singapore',
      DE: 'Singapur',
    },
  },
  ES: {
    code: 'ES',
    label: {
      EN: 'Spain',
      FR: 'Espagne',
      IT: 'Spagna',
      DE: 'Spanien',
    },
  },
  SE: {
    code: 'SE',
    label: {
      EN: 'Sweden',
      FR: 'Suède',
      IT: 'Svezia',
      DE: 'Schweden',
    },
  },
  CH: {
    code: 'CH',
    label: {
      EN: 'Switzerland',
      FR: 'Suisse',
      IT: 'Svizzera',
      DE: 'Schweiz',
    },
  },
  GB: {
    code: 'GB',
    label: {
      EN: 'United Kingdom',
      FR: 'Royaume-Uni',
      IT: 'Regno Unito',
      DE: 'Vereinigtes Königreich',
    },
  },
  US: {
    code: 'US',
    label: {
      EN: 'United States',
      FR: 'États-Unis',
      IT: 'Stati Uniti',
      DE: 'Vereinigte Staaten',
    },
  },
  AF: {
    code: 'AF',
    label: {
      EN: 'Afghanistan',
      FR: 'Afghanistan',
      IT: 'Afghanistan',
      DE: 'Afghanistan',
    },
  },
  AX: {
    code: 'AX',
    label: {
      EN: 'Åland Islands',
      FR: 'Îles Åland',
      IT: 'Isole Åland',
      DE: 'Ålandinseln',
    },
  },
  AL: {
    code: 'AL',
    label: {
      EN: 'Albania',
      FR: 'Albanie',
      IT: 'Albania',
      DE: 'Albanien',
    },
  },
  AN: {
    code: 'AN',
    label: {
      EN: 'Netherlands Antilles',
      FR: 'Antilles Néerlandaises',
      IT: 'Antille Olandesi',
      DE: 'Niederländische Antillen',
    },
  },
  DZ: {
    code: 'DZ',
    label: {
      EN: 'Algeria',
      FR: 'Algérie',
      IT: 'Algeria',
      DE: 'Algerien',
    },
  },
  AS: {
    code: 'AS',
    label: {
      EN: 'American Samoa',
      FR: 'Samoa Américaines',
      IT: 'Samoa Americane',
      DE: 'Amerikanisch-Samoa',
    },
  },
  AD: {
    code: 'AD',
    label: {
      EN: 'Andorra',
      FR: 'Andorre',
      IT: 'Andorra',
      DE: 'Andorra',
    },
  },
  AO: {
    code: 'AO',
    label: {
      EN: 'Angola',
      FR: 'Angola',
      IT: 'Angola',
      DE: 'Angola',
    },
  },
  AI: {
    code: 'AI',
    label: {
      EN: 'Anguilla',
      FR: 'Anguilla',
      IT: 'Anguilla',
      DE: 'Anguilla',
    },
  },
  AQ: {
    code: 'AQ',
    label: {
      EN: 'Antarctica',
      FR: 'Antarctique',
      IT: 'Antartide',
      DE: 'Antarktis',
    },
  },
  AG: {
    code: 'AG',
    label: {
      EN: 'Antigua and Barbuda',
      FR: 'Antigua-et-Barbuda',
      IT: 'Antigua e Barbuda',
      DE: 'Antigua und Barbuda',
    },
  },
  AR: {
    code: 'AR',
    label: {
      EN: 'Argentina',
      FR: 'Argentin',
      IT: 'Argentina',
      DE: 'Argentinien',
    },
  },
  AM: {
    code: 'AM',
    label: {
      EN: 'Armenia',
      FR: 'Arménie',
      IT: 'Armenia',
      DE: 'Armenien',
    },
  },
  AW: {
    code: 'AW',
    label: {
      EN: 'Aruba',
      FR: 'Aruba',
      IT: 'Aruba',
      DE: 'Aruba',
    },
  },
  AZ: {
    code: 'AZ',
    label: {
      EN: 'Azerbaijan',
      FR: 'Azerbaijan',
      IT: 'Azerbaigian',
      DE: 'Aserbaidschan',
    },
  },
  BS: {
    code: 'BS',
    label: {
      EN: 'Bahamas',
      FR: 'Bahamas',
      IT: 'Bahamas',
      DE: 'Bahamas',
    },
  },
  BH: {
    code: 'BH',
    label: {
      EN: 'Bahrain',
      FR: 'Bahreïn',
      IT: 'Bahrein',
      DE: 'Bahrain',
    },
  },
  BD: {
    code: 'BD',
    label: {
      EN: 'Bangladesh',
      FR: 'Bangladesh',
      IT: 'Bangladesh',
      DE: 'Bangladesch',
    },
  },
  BB: {
    code: 'BB',
    label: {
      EN: 'Barbados',
      FR: 'Barbade',
      IT: 'Barbados',
      DE: 'Barbados',
    },
  },
  BY: {
    code: 'BY',
    label: {
      EN: 'Belarus',
      FR: 'Biélorussie',
      IT: 'Bielorussia',
      DE: 'Belarus',
    },
  },
  BZ: {
    code: 'BZ',
    label: {
      EN: 'Belize',
      FR: 'Belize',
      IT: 'Belize',
      DE: 'Belize',
    },
  },
  BJ: {
    code: 'BJ',
    label: {
      EN: 'Benin',
      FR: 'Bènin',
      IT: 'Benin',
      DE: 'Benin',
    },
  },
  BM: {
    code: 'BM',
    label: {
      EN: 'Bermuda',
      FR: 'Bermudes',
      IT: 'Bermuda',
      DE: 'Bermuda',
    },
  },
  BT: {
    code: 'BT',
    label: {
      EN: 'Bhutan',
      FR: 'Bhoutan',
      IT: 'Bhutan',
      DE: 'Bhutan',
    },
  },
  BO: {
    code: 'BO',
    label: {
      EN: 'Bolivia',
      FR: 'Bolivie',
      IT: 'Bolivia',
      DE: 'Bolivien',
    },
  },
  BQ: {
    code: 'BQ',
    label: {
      EN: 'Sint Eustatius and Saba Bonaire',
      FR: 'Saint-Eustache et Saba Bonaire',
      IT: 'Isole BES',
      DE: 'Bonaire, Sint Eustatius und Saba',
    },
  },
  BA: {
    code: 'BA',
    label: {
      EN: 'Bosnia and Herzegovina',
      FR: 'Bosnie-Herzegovine',
      IT: 'Bosnia ed Erzegovina',
      DE: 'Bosnien und Herzegowina',
    },
  },
  BW: {
    code: 'BW',
    label: {
      EN: 'Botswana',
      FR: 'Botswana',
      IT: 'Botswana',
      DE: 'Botswana',
    },
  },
  BV: {
    code: 'BV',
    label: {
      EN: 'Bouvet Island',
      FR: 'Île Bouvet',
      IT: 'Isola Bouvet',
      DE: 'Bouvetinsel',
    },
  },
  BR: {
    code: 'BR',
    label: {
      EN: 'Brazil',
      FR: 'Brésil',
      IT: 'Brasile',
      DE: 'Brasilien',
    },
  },
  IO: {
    code: 'IO',
    label: {
      EN: 'British Indian Ocean Territory',
      FR: "Territoire Britannique de l'Océan Indien",
      IT: "Territorio Britannico dell'Oceano Indiano",
      DE: 'Britisches Territorium im Indischen Ozean',
    },
  },
  BN: {
    code: 'BN',
    label: {
      EN: 'Brunei Darussalam',
      FR: 'Brunéi Darussalam',
      IT: 'Brunei',
      DE: 'Brunei',
    },
  },
  BG: {
    code: 'BG',
    label: {
      EN: 'Bulgaria',
      FR: 'Bulgarie',
      IT: 'Bulgaria',
      DE: 'Bulgarien',
    },
  },
  BF: {
    code: 'BF',
    label: {
      EN: 'Burkina Faso',
      FR: 'Burkina Faso',
      IT: 'Burkina Faso',
      DE: 'Burkina Faso',
    },
  },
  BI: {
    code: 'BI',
    label: {
      EN: 'Burundi',
      FR: 'Burundi',
      IT: 'Burundi',
      DE: 'Burundi',
    },
  },
  KH: {
    code: 'KH',
    label: {
      EN: 'Cambodia',
      FR: 'Cambodge',
      IT: 'Cambogia',
      DE: 'Kambodscha',
    },
  },
  CM: {
    code: 'CM',
    label: {
      EN: 'Cameroon',
      FR: 'Cameroon',
      IT: 'Camerun',
      DE: 'Kamerun',
    },
  },
  CV: {
    code: 'CV',
    label: {
      EN: 'Cape Verde',
      FR: 'Cap-Vert',
      IT: 'Capo Verde',
      DE: 'Kap Verde',
    },
  },
  KY: {
    code: 'KY',
    label: {
      EN: 'Cayman Islands',
      FR: 'Îles Caïmans',
      IT: 'Isole Cayman',
      DE: 'Kaimaninseln',
    },
  },
  CF: {
    code: 'CF',
    label: {
      EN: 'Central African Republic',
      FR: 'République Centrafricaine',
      IT: 'Repubblica Centrafricana',
      DE: 'Zentralafrikanische Republik',
    },
  },
  TD: {
    code: 'TD',
    label: {
      EN: 'Chad',
      FR: 'Tchad',
      IT: 'Ciad',
      DE: 'Tschad',
    },
  },
  CL: {
    code: 'CL',
    label: {
      EN: 'Chile',
      FR: 'Chili',
      IT: 'Cile',
      DE: 'Chile',
    },
  },
  CN: {
    code: 'CN',
    label: {
      EN: 'China',
      FR: 'Chine',
      IT: 'Cina',
      DE: 'China',
    },
  },
  CX: {
    code: 'CX',
    label: {
      EN: 'Christmas Island',
      FR: 'Île Christmas',
      IT: 'Isola di Natale',
      DE: 'Weihnachtsinsel',
    },
  },
  CC: {
    code: 'CC',
    label: {
      EN: 'Cocos (Keeling) Islands',
      FR: 'Îles Cocos (Keeling)',
      IT: 'Isole Cocos (Keeling)',
      DE: 'Kokosinseln',
    },
  },
  CO: {
    code: 'CO',
    label: {
      EN: 'Colombia',
      FR: 'Colombie',
      IT: 'Colombia',
      DE: 'Kolumbien',
    },
  },
  KM: {
    code: 'KM',
    label: {
      EN: 'Comoros',
      FR: 'Comore',
      IT: 'Comore',
      DE: 'Komoren',
    },
  },
  CG: {
    code: 'CG',
    label: {
      EN: 'Congo',
      FR: 'Congo',
      IT: 'Congo',
      DE: 'Kongo',
    },
  },
  CD: {
    code: 'CD',
    label: {
      EN: 'Congo, the Democratic Republic of the',
      FR: 'République Démocratique du Congo',
      IT: 'Repubblica Democratica del Congo',
      DE: 'Demokratische Republik Kongo',
    },
  },
  CK: {
    code: 'CK',
    label: {
      EN: 'Cook Islands',
      FR: 'Îles Cook',
      IT: 'Isole Cook',
      DE: 'Cookinseln',
    },
  },
  CR: {
    code: 'CR',
    label: {
      EN: 'Costa Rica',
      FR: 'Costa Rica',
      IT: 'Costa Rica',
      DE: 'Costa Rica',
    },
  },
  CI: {
    code: 'CI',
    label: {
      EN: "Côte d'Ivoire",
      FR: "Côte d'Ivoire",
      IT: "Costa d'Avorio",
      DE: 'Elfenbeinküste',
    },
  },
  HR: {
    code: 'HR',
    label: {
      EN: 'Croatia',
      FR: 'Croatie',
      IT: 'Croazia',
      DE: 'Kroatien',
    },
  },
  CU: {
    code: 'CU',
    label: {
      EN: 'Cuba',
      FR: 'Cuba',
      IT: 'Cuba',
      DE: 'Kuba',
    },
  },
  CW: {
    code: 'CW',
    label: {
      EN: 'Curaçao',
      FR: 'Curaçao',
      IT: 'Curaçao',
      DE: 'Curaçao',
    },
  },
  CY: {
    code: 'CY',
    label: {
      EN: 'Cyprus',
      FR: 'Chypre',
      IT: 'Cipro',
      DE: 'Zypern',
    },
  },
  CZ: {
    code: 'CZ',
    label: {
      EN: 'Czech Republic',
      FR: 'République Tchèque',
      IT: 'Repubblica Ceca',
      DE: 'Tschechische Republik',
    },
  },
  DJ: {
    code: 'DJ',
    label: {
      EN: 'Djibouti',
      FR: 'Djibouti',
      IT: 'Gibuti',
      DE: 'Dschibuti',
    },
  },
  DM: {
    code: 'DM',
    label: {
      EN: 'Dominica',
      FR: 'Dominique',
      IT: 'Dominica',
      DE: 'Dominica',
    },
  },
  DO: {
    code: 'DO',
    label: {
      EN: 'Dominican Republic',
      FR: 'République Dominicaine',
      IT: 'Repubblica Dominicana',
      DE: 'Dominikanische Republik',
    },
  },
  EC: {
    code: 'EC',
    label: {
      EN: 'Ecuador',
      FR: 'Équateur',
      IT: 'Ecuador',
      DE: 'Ecuador',
    },
  },
  EG: {
    code: 'EG',
    label: {
      EN: 'Egypt',
      FR: 'Egypte',
      IT: 'Egitto',
      DE: 'Ägypten',
    },
  },
  SV: {
    code: 'SV',
    label: {
      EN: 'El Salvador',
      FR: 'El Salvador',
      IT: 'El Salvador',
      DE: 'El Salvador',
    },
  },
  GQ: {
    code: 'GQ',
    label: {
      EN: 'Equatorial Guinea',
      FR: 'Guinée Équatoriale',
      IT: 'Guinea Equatoriale',
      DE: 'Äquatorialguinea',
    },
  },
  ER: {
    code: 'ER',
    label: {
      EN: 'Eritrea',
      FR: 'Érythrée',
      IT: 'Eritrea',
      DE: 'Eritrea',
    },
  },
  EE: {
    code: 'EE',
    label: {
      EN: 'Estonia',
      FR: 'Estonie',
      IT: 'Estonia',
      DE: 'Estland',
    },
  },
  ET: {
    code: 'ET',
    label: {
      EN: 'Ethiopia',
      FR: 'Ethiopie',
      IT: 'Etiopia',
      DE: 'Äthiopien',
    },
  },
  FK: {
    code: 'FK',
    label: {
      EN: 'Falkland Islands (Malvinas)',
      FR: 'Îles Falkland (Malvinas)',
      IT: 'Isole Falkland (Malvinas)',
      DE: 'Falklandinseln',
    },
  },
  FO: {
    code: 'FO',
    label: {
      EN: 'Faroe Islands',
      FR: 'Îles Féroé',
      IT: 'Fær Øer',
      DE: 'Färöer-Inseln',
    },
  },
  FJ: {
    code: 'FJ',
    label: {
      EN: 'Fiji',
      FR: 'Fidji',
      IT: 'Figi',
      DE: 'Fidschi',
    },
  },
  FI: {
    code: 'FI',
    label: {
      EN: 'Finland',
      FR: 'Finlande',
      IT: 'Finlandia',
      DE: 'Finnland',
    },
  },
  GF: {
    code: 'GF',
    label: {
      EN: 'French Guiana',
      FR: 'Guyane Française',
      IT: 'Guyana Francese',
      DE: 'Französisch-Guayana',
    },
  },
  PF: {
    code: 'PF',
    label: {
      EN: 'French Polynesia',
      FR: 'Polynésie Française',
      IT: 'Polinesia Francese',
      DE: 'Französisch-Polynesien',
    },
  },
  TF: {
    code: 'TF',
    label: {
      EN: 'French Southern Territories',
      FR: 'Territoires français du Sud',
      IT: 'Terre australi e antartiche francesi',
      DE: 'Französische Süd- und Antarktisgebiete',
    },
  },
  GA: {
    code: 'GA',
    label: {
      EN: 'Gabon',
      FR: 'Gabon',
      IT: 'Gabon',
      DE: 'Gabun',
    },
  },
  GM: {
    code: 'GM',
    label: {
      EN: 'Gambia',
      FR: 'Gambie',
      IT: 'Gambia',
      DE: 'Gambia',
    },
  },
  GE: {
    code: 'GE',
    label: {
      EN: 'Georgia',
      FR: 'Géorgie',
      IT: 'Georgia',
      DE: 'Georgien',
    },
  },
  GH: {
    code: 'GH',
    label: {
      EN: 'Ghana',
      FR: 'Ghana',
      IT: 'Ghana',
      DE: 'Ghana',
    },
  },
  GI: {
    code: 'GI',
    label: {
      EN: 'Gibraltar',
      FR: 'Gibraltar',
      IT: 'Gibilterra',
      DE: 'Gibraltar',
    },
  },
  GL: {
    code: 'GL',
    label: {
      EN: 'Greenland',
      FR: 'Groenland',
      IT: 'Groenlandia',
      DE: 'Grönland',
    },
  },
  GD: {
    code: 'GD',
    label: {
      EN: 'Grenada',
      FR: 'Grenada',
      IT: 'Grenada',
      DE: 'Grenada',
    },
  },
  GP: {
    code: 'GP',
    label: {
      EN: 'Guadeloupe',
      FR: 'Guadeloupe',
      IT: 'Guadalupa',
      DE: 'Guadeloupe',
    },
  },
  GU: {
    code: 'GU',
    label: {
      EN: 'Guam',
      FR: 'Guam',
      IT: 'Guam',
      DE: 'Guam',
    },
  },
  GT: {
    code: 'GT',
    label: {
      EN: 'Guatemala',
      FR: 'Guatemala',
      IT: 'Guatemala',
      DE: 'Guatemala',
    },
  },
  GG: {
    code: 'GG',
    label: {
      EN: 'Guernsey',
      FR: 'Guernsey',
      IT: 'Guernsey',
      DE: 'Guernsey',
    },
  },
  GN: {
    code: 'GN',
    label: {
      EN: 'Guinea',
      FR: 'Guinée',
      IT: 'Guinea',
      DE: 'Guinea',
    },
  },
  GW: {
    code: 'GW',
    label: {
      EN: 'Guinea-Bissau',
      FR: 'Guinée-Bissau',
      IT: 'Guinea-Bissau',
      DE: 'Guinea-Bissau',
    },
  },
  GY: {
    code: 'GY',
    label: {
      EN: 'Guyana',
      FR: 'Guyane',
      IT: 'Guyana',
      DE: 'Guyana',
    },
  },
  HT: {
    code: 'HT',
    label: {
      EN: 'Haiti',
      FR: 'Haïti',
      IT: 'Haiti',
      DE: 'Haiti',
    },
  },
  HM: {
    code: 'HM',
    label: {
      EN: 'Heard Island and McDonald Islands',
      FR: 'Îles Heard et McDonald',
      IT: 'Isole Heard e McDonald',
      DE: 'Heard und McDonaldinseln',
    },
  },
  VA: {
    code: 'VA',
    label: {
      EN: 'Holy See (Vatican City State)',
      FR: 'Cité du Vatican',
      IT: 'Città del Vaticano',
      DE: 'Vatikanstadt',
    },
  },
  HN: {
    code: 'HN',
    label: {
      EN: 'Honduras',
      FR: 'Honduras',
      IT: 'Honduras',
      DE: 'Honduras',
    },
  },
  HU: {
    code: 'HU',
    label: {
      EN: 'Hungary',
      FR: 'Hongrie',
      IT: 'Ungheria',
      DE: 'Ungarn',
    },
  },
  IS: {
    code: 'IS',
    label: {
      EN: 'Iceland',
      FR: 'Islande',
      IT: 'Islanda',
      DE: 'Island',
    },
  },
  IN: {
    code: 'IN',
    label: {
      EN: 'India',
      FR: 'Inde',
      IT: 'India',
      DE: 'Indien',
    },
  },
  ID: {
    code: 'ID',
    label: {
      EN: 'Indonesia',
      FR: 'Indonésie',
      IT: 'Indonesia',
      DE: 'Indonesien',
    },
  },
  IR: {
    code: 'IR',
    label: {
      EN: 'Iran, Islamic Republic of',
      FR: 'Iran',
      IT: 'Iran',
      DE: 'Iran',
    },
  },
  IQ: {
    code: 'IQ',
    label: {
      EN: 'Iraq',
      FR: 'Iraq',
      IT: 'Iraq',
      DE: 'Irak',
    },
  },
  IE: {
    code: 'IE',
    label: {
      EN: 'Ireland',
      FR: 'Irlande',
      IT: 'Irlanda',
      DE: 'Irland',
    },
  },
  IM: {
    code: 'IM',
    label: {
      EN: 'Isle of Man',
      FR: 'Ile de Man',
      IT: 'Isola di Man',
      DE: 'Isle of Man',
    },
  },
  IL: {
    code: 'IL',
    label: {
      EN: 'Israel',
      FR: 'Israël',
      IT: 'Israele',
      DE: 'Israel',
    },
  },
  JM: {
    code: 'JM',
    label: {
      EN: 'Jamaica',
      FR: 'Jamaïque',
      IT: 'Jamaica',
      DE: 'Jamaika',
    },
  },
  JP: {
    code: 'JP',
    label: {
      EN: 'Japan',
      FR: 'Japon',
      IT: 'Giappone',
      DE: 'Japan',
    },
  },
  JE: {
    code: 'JE',
    label: {
      EN: 'Jersey',
      FR: 'Jersey',
      IT: 'Jersey',
      DE: 'Jersey',
    },
  },
  JO: {
    code: 'JO',
    label: {
      EN: 'Jordan',
      FR: 'Jordan',
      IT: 'Giordania',
      DE: 'Jordanien',
    },
  },
  KZ: {
    code: 'KZ',
    label: {
      EN: 'Kazakhstan',
      FR: 'Kazakhstan',
      IT: 'Kazakistan',
      DE: 'Kasachstan',
    },
  },
  KE: {
    code: 'KE',
    label: {
      EN: 'Kenya',
      FR: 'Kenya',
      IT: 'Kenya',
      DE: 'Kenia',
    },
  },
  KI: {
    code: 'KI',
    label: {
      EN: 'Kiribati',
      FR: 'Kiribati',
      IT: 'Kiribati',
      DE: 'Kiribati',
    },
  },
  KP: {
    code: 'KP',
    label: {
      EN: "Korea, Democratic People's Republic of",
      FR: 'Corée, République Populaire Démocratique de',
      IT: 'Corea del Nord',
      DE: 'Nordkorea',
    },
  },
  KR: {
    code: 'KR',
    label: {
      EN: 'Korea, Republic of',
      FR: 'Corée, République de',
      IT: 'Corea del Sud',
      DE: 'Südkorea',
    },
  },
  KW: {
    code: 'KW',
    label: {
      EN: 'Kuwait',
      FR: 'Kuwait',
      IT: 'Kuwait',
      DE: 'Kuwait',
    },
  },
  KG: {
    code: 'KG',
    label: {
      EN: 'Kyrgyzstan',
      FR: 'Kirghizistan',
      IT: 'Kirghizistan',
      DE: 'Kirgisistan',
    },
  },
  LA: {
    code: 'LA',
    label: {
      EN: "Lao People's Democratic Republic",
      FR: 'République Démocratique Populaire Lao',
      IT: 'Laos',
      DE: 'Laos',
    },
  },
  LV: {
    code: 'LV',
    label: {
      EN: 'Latvia',
      FR: 'Lettonie',
      IT: 'Lettonia',
      DE: 'Lettland',
    },
  },
  LB: {
    code: 'LB',
    label: {
      EN: 'Lebanon',
      FR: 'Liban',
      IT: 'Libano',
      DE: 'Libanon',
    },
  },
  LS: {
    code: 'LS',
    label: {
      EN: 'Lesotho',
      FR: 'Lesotho',
      IT: 'Lesotho',
      DE: 'Lesotho',
    },
  },
  LR: {
    code: 'LR',
    label: {
      EN: 'Liberia',
      FR: 'Libéria',
      IT: 'Liberia',
      DE: 'Liberia',
    },
  },
  LY: {
    code: 'LY',
    label: {
      EN: 'Libya',
      FR: 'Libye',
      IT: 'Libia',
      DE: 'Libyen',
    },
  },
  LI: {
    code: 'LI',
    label: {
      EN: 'Liechtenstein',
      FR: 'Liechtenstein',
      IT: 'Liechtenstein',
      DE: 'Liechtenstein',
    },
  },
  LT: {
    code: 'LT',
    label: {
      EN: 'Lithuania',
      FR: 'Lituanie',
      IT: 'Lituania',
      DE: 'Litauen',
    },
  },
  MO: {
    code: 'MO',
    label: {
      EN: 'Macao',
      FR: 'Macao',
      IT: 'Macao',
      DE: 'Macao',
    },
  },
  MK: {
    code: 'MK',
    label: {
      EN: 'Macedonia, the Former Yugoslav Republic of',
      FR: 'Macédoine, Ancienne République Yougoslave de',
      IT: 'Macedonia',
      DE: 'Nordmazedonien',
    },
  },
  MG: {
    code: 'MG',
    label: {
      EN: 'Madagascar',
      FR: 'Madagascar',
      IT: 'Madagascar',
      DE: 'Madagaskar',
    },
  },
  MW: {
    code: 'MW',
    label: {
      EN: 'Malawi',
      FR: 'Malawi',
      IT: 'Malawi',
      DE: 'Malawi',
    },
  },
  MY: {
    code: 'MY',
    label: {
      EN: 'Malaysia',
      FR: 'Malaisie',
      IT: 'Malesia',
      DE: 'Malaysia',
    },
  },
  MV: {
    code: 'MV',
    label: {
      EN: 'Maldives',
      FR: 'Maldives',
      IT: 'Maldive',
      DE: 'Malediven',
    },
  },
  ML: {
    code: 'ML',
    label: {
      EN: 'Mali',
      FR: 'Mali',
      IT: 'Mali',
      DE: 'Mali',
    },
  },
  MT: {
    code: 'MT',
    label: {
      EN: 'Malta',
      FR: 'Malte',
      IT: 'Malta',
      DE: 'Malta',
    },
  },
  MH: {
    code: 'MH',
    label: {
      EN: 'Marshall Islands',
      FR: 'Iles Marshall',
      IT: 'Isole Marshall',
      DE: 'Marshallinseln',
    },
  },
  MQ: {
    code: 'MQ',
    label: {
      EN: 'Martinique',
      FR: 'Martinique',
      IT: 'Martinica',
      DE: 'Martinique',
    },
  },
  MR: {
    code: 'MR',
    label: {
      EN: 'Mauritania',
      FR: 'Mauritanie',
      IT: 'Mauritania',
      DE: 'Mauretanien',
    },
  },
  MU: {
    code: 'MU',
    label: {
      EN: 'Mauritius',
      FR: 'Maurice',
      IT: 'Mauritius',
      DE: 'Mauritius',
    },
  },
  YT: {
    code: 'YT',
    label: {
      EN: 'Mayotte',
      FR: 'Mayotte',
      IT: 'Mayotte',
      DE: 'Mayotte',
    },
  },
  MX: {
    code: 'MX',
    label: {
      EN: 'Mexico',
      FR: 'Mexique',
      IT: 'Messico',
      DE: 'Mexiko',
    },
  },
  FM: {
    code: 'FM',
    label: {
      EN: 'Micronesia, Federated States of',
      FR: 'Micronésie, États Fédérés de',
      IT: 'Micronesia',
      DE: 'Mikronesien',
    },
  },
  MD: {
    code: 'MD',
    label: {
      EN: 'Moldova, Republic of',
      FR: 'Moldova, République de',
      IT: 'Moldavia',
      DE: 'Moldau',
    },
  },
  MC: {
    code: 'MC',
    label: {
      EN: 'Monaco',
      FR: 'Monaco',
      IT: 'Monaco',
      DE: 'Monaco',
    },
  },
  MN: {
    code: 'MN',
    label: {
      EN: 'Mongolia',
      FR: 'Mongolie',
      IT: 'Mongolia',
      DE: 'Mongolei',
    },
  },
  ME: {
    code: 'ME',
    label: {
      EN: 'Montenegro',
      FR: 'Monténégro',
      IT: 'Montenegro',
      DE: 'Montenegro',
    },
  },
  MS: {
    code: 'MS',
    label: {
      EN: 'Montserrat',
      FR: 'Montserrat',
      IT: 'Montserrat',
      DE: 'Montserrat',
    },
  },
  MA: {
    code: 'MA',
    label: {
      EN: 'Morocco',
      FR: 'Maroc',
      IT: 'Marocco',
      DE: 'Marokko',
    },
  },
  MZ: {
    code: 'MZ',
    label: {
      EN: 'Mozambique',
      FR: 'Mozambique',
      IT: 'Mozambico',
      DE: 'Mosambik',
    },
  },
  MM: {
    code: 'MM',
    label: {
      EN: 'Myanmar',
      FR: 'Myanmar',
      IT: 'Birmania',
      DE: 'Myanmar',
    },
  },
  NA: {
    code: 'NA',
    label: {
      EN: 'Namibia',
      FR: 'Namibie',
      IT: 'Namibia',
      DE: 'Namibia',
    },
  },
  NR: {
    code: 'NR',
    label: {
      EN: 'Nauru',
      FR: 'Nauru',
      IT: 'Nauru',
      DE: 'Nauru',
    },
  },
  NP: {
    code: 'NP',
    label: {
      EN: 'Nepal',
      FR: 'Népal',
      IT: 'Nepal',
      DE: 'Nepal',
    },
  },
  NC: {
    code: 'NC',
    label: {
      EN: 'New Caledonia',
      FR: 'Nouvelle Calédonie',
      IT: 'Nuova Caledonia',
      DE: 'Neukaledonien',
    },
  },
  NZ: {
    code: 'NZ',
    label: {
      EN: 'New Zealand',
      FR: 'Nouvelle-Zélande',
      IT: 'Nuova Zelanda',
      DE: 'Neuseeland',
    },
  },
  NI: {
    code: 'NI',
    label: {
      EN: 'Nicaragua',
      FR: 'Nicaragua',
      IT: 'Nicaragua',
      DE: 'Nicaragua',
    },
  },
  NE: {
    code: 'NE',
    label: {
      EN: 'Niger',
      FR: 'Niger',
      IT: 'Niger',
      DE: 'Niger',
    },
  },
  NG: {
    code: 'NG',
    label: {
      EN: 'Nigeria',
      FR: 'Nigeria',
      IT: 'Nigeria',
      DE: 'Nigeria',
    },
  },
  NU: {
    code: 'NU',
    label: {
      EN: 'Niue',
      FR: 'Niue',
      IT: 'Niue',
      DE: 'Niue',
    },
  },
  NF: {
    code: 'NF',
    label: {
      EN: 'Norfolk Island',
      FR: "l'Ile de Norfolk",
      IT: 'Isola Norfolk',
      DE: 'Norfolkinsel',
    },
  },
  MP: {
    code: 'MP',
    label: {
      EN: 'Northern Mariana Islands',
      FR: 'Îles Mariannes du Nord',
      IT: 'Isole Marianne Settentrionali',
      DE: 'Nördliche Marianen',
    },
  },
  OM: {
    code: 'OM',
    label: {
      EN: 'Oman',
      FR: 'Oman',
      IT: 'Oman',
      DE: 'Oman',
    },
  },
  PK: {
    code: 'PK',
    label: {
      EN: 'Pakistan',
      FR: 'Pakistan',
      IT: 'Pakistan',
      DE: 'Pakistan',
    },
  },
  PW: {
    code: 'PW',
    label: {
      EN: 'Palau',
      FR: 'Palau',
      IT: 'Palau',
      DE: 'Palau',
    },
  },
  PS: {
    code: 'PS',
    label: {
      EN: 'Palestine, State of',
      FR: 'Palestine, État de',
      IT: 'Palestina',
      DE: 'Palästina',
    },
  },
  PA: {
    code: 'PA',
    label: {
      EN: 'Panama',
      FR: 'Panama',
      IT: 'Panama',
      DE: 'Panama',
    },
  },
  PG: {
    code: 'PG',
    label: {
      EN: 'Papua New Guinea',
      FR: 'Papouasie Nouvelle Guinée',
      IT: 'Papua Nuova Guinea',
      DE: 'Papua-Neuguinea',
    },
  },
  PY: {
    code: 'PY',
    label: {
      EN: 'Paraguay',
      FR: 'Paraguay',
      IT: 'Paraguay',
      DE: 'Paraguay',
    },
  },
  PE: {
    code: 'PE',
    label: {
      EN: 'Peru',
      FR: 'Pérou',
      IT: 'Peru',
      DE: 'Peru',
    },
  },
  PH: {
    code: 'PH',
    label: {
      EN: 'Philippines',
      FR: 'Philippines',
      IT: 'Filippine',
      DE: 'Philippinen',
    },
  },
  PN: {
    code: 'PN',
    label: {
      EN: 'Pitcairn',
      FR: 'Pitcairn',
      IT: 'Isole Pitcairn',
      DE: 'Pitcairninseln',
    },
  },
  PL: {
    code: 'PL',
    label: {
      EN: 'Poland',
      FR: 'Pologne',
      IT: 'Polonia',
      DE: 'Polen',
    },
  },
  PR: {
    code: 'PR',
    label: {
      EN: 'Puerto Rico',
      FR: 'Porto Rico',
      IT: 'Porto Rico',
      DE: 'Puerto Rico',
    },
  },
  QA: {
    code: 'QA',
    label: {
      EN: 'Qatar',
      FR: 'Qatar',
      IT: 'Qatar',
      DE: 'Katar',
    },
  },
  RE: {
    code: 'RE',
    label: {
      EN: 'Reunion',
      FR: 'Réunion',
      IT: 'Riunione',
      DE: 'Réunion',
    },
  },
  RO: {
    code: 'RO',
    label: {
      EN: 'Romania',
      FR: 'Roumanie',
      IT: 'Romania',
      DE: 'Rumänien',
    },
  },
  RU: {
    code: 'RU',
    label: {
      EN: 'Russian Federation',
      FR: 'Fédération Russe',
      IT: 'Russia',
      DE: 'Russland',
    },
  },
  RW: {
    code: 'RW',
    label: {
      EN: 'Rwanda',
      FR: 'Rwanda',
      IT: 'Ruanda',
      DE: 'Ruanda',
    },
  },
  BL: {
    code: 'BL',
    label: {
      EN: 'Saint Barthélemy',
      FR: 'Saint Barthélemy',
      IT: 'Saint-Barthélemy',
      DE: 'Saint-Barthélemy',
    },
  },
  SH: {
    code: 'SH',
    label: {
      EN: 'Saint Helena',
      FR: 'Sainte-Hélène',
      IT: "Sant'Elena",
      DE: 'St. Helena',
    },
  },
  KN: {
    code: 'KN',
    label: {
      EN: 'Saint Kitts and Nevis',
      FR: 'Saint-Christophe-et-Niévès',
      IT: 'Saint Kitts e Nevis',
      DE: 'St. Kitts und Nevis',
    },
  },
  LC: {
    code: 'LC',
    label: {
      EN: 'Saint Lucia',
      FR: 'Sainte-Lucie',
      IT: 'Santa Lucia',
      DE: 'St. Lucia',
    },
  },
  MF: {
    code: 'MF',
    label: {
      EN: 'Saint Martin (French part)',
      FR: 'Saint Martin (partie française)',
      IT: 'Saint-Martin',
      DE: 'Saint-Martin',
    },
  },
  PM: {
    code: 'PM',
    label: {
      EN: 'Saint Pierre and Miquelon',
      FR: 'Saint-Pierre-et-Miquelon',
      IT: 'Saint-Pierre e Miquelon',
      DE: 'St. Pierre und Miquelon',
    },
  },
  VC: {
    code: 'VC',
    label: {
      EN: 'Saint Vincent and the Grenadines',
      FR: 'Saint-Vincent-et-les-Grenadines',
      IT: 'Saint Vincent e Grenadine',
      DE: 'St. Vincent und die Grenadinen',
    },
  },
  WS: {
    code: 'WS',
    label: {
      EN: 'Samoa',
      FR: 'Samoa',
      IT: 'Samoa',
      DE: 'Samoa',
    },
  },
  SM: {
    code: 'SM',
    label: {
      EN: 'San Marino',
      FR: 'Saint Marin',
      IT: 'San Marino',
      DE: 'San Marino',
    },
  },
  ST: {
    code: 'ST',
    label: {
      EN: 'Sao Tome and Principe',
      FR: 'Sao Tomé et Principe',
      IT: 'Sao Tome e Principe',
      DE: 'São Tomé und Príncipe',
    },
  },
  SA: {
    code: 'SA',
    label: {
      EN: 'Saudi Arabia',
      FR: 'Arabie Saoudite',
      IT: 'Arabia Saudita',
      DE: 'Saudi-Arabien',
    },
  },
  SN: {
    code: 'SN',
    label: {
      EN: 'Senegal',
      FR: 'Sénégal',
      IT: 'Senegal',
      DE: 'Senegal',
    },
  },
  RS: {
    code: 'RS',
    label: {
      EN: 'Serbia',
      FR: 'Serbie',
      IT: 'Serbia',
      DE: 'Serbien',
    },
  },
  SC: {
    code: 'SC',
    label: {
      EN: 'Seychelles',
      FR: 'Seychelles',
      IT: 'Seychelles',
      DE: 'Seychellen',
    },
  },
  SL: {
    code: 'SL',
    label: {
      EN: 'Sierra Leone',
      FR: 'Sierra Leone',
      IT: 'Sierra Leone',
      DE: 'Sierra Leone',
    },
  },
  SX: {
    code: 'SX',
    label: {
      EN: 'Sint Maarten (Dutch part)',
      FR: 'Sint Maarten (partie néerlandaise)',
      IT: 'Sint Maarten',
      DE: 'Sint Maarten',
    },
  },
  SK: {
    code: 'SK',
    label: {
      EN: 'Slovakia',
      FR: 'Slovaquie',
      IT: 'Slovacchia',
      DE: 'Slowakei',
    },
  },
  SI: {
    code: 'SI',
    label: {
      EN: 'Slovenia',
      FR: 'Slovénie',
      IT: 'Slovenia',
      DE: 'Slowenien',
    },
  },
  SB: {
    code: 'SB',
    label: {
      EN: 'Solomon Islands',
      FR: 'Les îles Salomon',
      IT: 'Isole Salomone',
      DE: 'Salomonen',
    },
  },
  SO: {
    code: 'SO',
    label: {
      EN: 'Somalia',
      FR: 'Somalie',
      IT: 'Somalia',
      DE: 'Somalia',
    },
  },
  ZA: {
    code: 'ZA',
    label: {
      EN: 'South Africa',
      FR: 'Afrique du Sud',
      IT: 'Sud Africa',
      DE: 'Südafrika',
    },
  },
  GS: {
    code: 'GS',
    label: {
      EN: 'South Georgia and the South Sandwich Islands',
      FR: 'Géorgie du Sud et îles Sandwich du Sud',
      IT: 'Georgia del Sud e isole Sandwich meridionali',
      DE: 'Südgeorgien und die Südlichen Sandwichinseln',
    },
  },
  SS: {
    code: 'SS',
    label: {
      EN: 'South Sudan',
      FR: 'Soudan du sud',
      IT: 'Sudan del Sud',
      DE: 'Südsudan',
    },
  },
  LK: {
    code: 'LK',
    label: {
      EN: 'Sri Lanka',
      FR: 'Sri Lanka',
      IT: 'Sri Lanka',
      DE: 'Sri Lanka',
    },
  },
  SD: {
    code: 'SD',
    label: {
      EN: 'Sudan',
      FR: 'Soudan',
      IT: 'Sudan',
      DE: 'Sudan',
    },
  },
  SR: {
    code: 'SR',
    label: {
      EN: 'Suriname',
      FR: 'Suriname',
      IT: 'Suriname',
      DE: 'Suriname',
    },
  },
  SJ: {
    code: 'SJ',
    label: {
      EN: 'Svalbard and Jan Mayen',
      FR: 'Svalbard et Jan Mayen',
      IT: 'Svalbard e Jan Mayen',
      DE: 'Svalbard und Jan Mayen',
    },
  },
  SZ: {
    code: 'SZ',
    label: {
      EN: 'Swaziland',
      FR: 'Swaziland',
      IT: 'Swaziland',
      DE: 'Eswatini',
    },
  },
  SY: {
    code: 'SY',
    label: {
      EN: 'Syrian Arab Republic',
      FR: 'République Arabe Syrienne',
      IT: 'Siria',
      DE: 'Syrien',
    },
  },
  TW: {
    code: 'TW',
    label: {
      EN: 'Taiwan',
      FR: 'Taiwan',
      IT: 'Taiwan',
      DE: 'Taiwan',
    },
  },
  TJ: {
    code: 'TJ',
    label: {
      EN: 'Tajikistan',
      FR: 'Tadjikistan',
      IT: 'Tagikistan',
      DE: 'Tadschikistan',
    },
  },
  TZ: {
    code: 'TZ',
    label: {
      EN: 'Tanzania',
      FR: 'Tanzanie',
      IT: 'Tanzania',
      DE: 'Tansania',
    },
  },
  TH: {
    code: 'TH',
    label: {
      EN: 'Thailand',
      FR: 'Thaïlande',
      IT: 'Thailandia',
      DE: 'Thailand',
    },
  },
  TL: {
    code: 'TL',
    label: {
      EN: 'Timor-Leste',
      FR: 'Timor-Leste',
      IT: 'Timor Est',
      DE: 'Osttimor',
    },
  },
  TG: {
    code: 'TG',
    label: {
      EN: 'Togo',
      FR: 'Togo',
      IT: 'Togo',
      DE: 'Togo',
    },
  },
  TK: {
    code: 'TK',
    label: {
      EN: 'Tokelau',
      FR: 'Tokelau',
      IT: 'Tokelau',
      DE: 'Tokelau',
    },
  },
  TO: {
    code: 'TO',
    label: {
      EN: 'Tonga',
      FR: 'Tonga',
      IT: 'Tonga',
      DE: 'Tonga',
    },
  },
  TT: {
    code: 'TT',
    label: {
      EN: 'Trinidad and Tobago',
      FR: 'Trinité-et-Tobago',
      IT: 'Trinidad e Tobago',
      DE: 'Trinidad und Tobago',
    },
  },
  TN: {
    code: 'TN',
    label: {
      EN: 'Tunisia',
      FR: 'Tunisie',
      IT: 'Tunisia',
      DE: 'Tunesien',
    },
  },
  TR: {
    code: 'TR',
    label: {
      EN: 'Turkey',
      FR: 'Turquie',
      IT: 'Turchia',
      DE: 'Türkei',
    },
  },
  TM: {
    code: 'TM',
    label: {
      EN: 'Turkmenistan',
      FR: 'Turkménistan',
      IT: 'Turkmenistan',
      DE: 'Turkmenistan',
    },
  },
  TC: {
    code: 'TC',
    label: {
      EN: 'Turks and Caicos Islands',
      FR: 'îles Turques-et-Caïques',
      IT: 'Turks e Caicos',
      DE: 'Turks- und Caicosinseln',
    },
  },
  TV: {
    code: 'TV',
    label: {
      EN: 'Tuvalu',
      FR: 'Tuvalu',
      IT: 'Tuvalu',
      DE: 'Tuvalu',
    },
  },
  UG: {
    code: 'UG',
    label: {
      EN: 'Uganda',
      FR: 'Ouganda',
      IT: 'Uganda',
      DE: 'Uganda',
    },
  },
  UA: {
    code: 'UA',
    label: {
      EN: 'Ukraine',
      FR: 'Ukraine',
      IT: 'Ucraina',
      DE: 'Ukraine',
    },
  },
  AE: {
    code: 'AE',
    label: {
      EN: 'United Arab Emirates',
      FR: 'Emirats Arabes Unis',
      IT: 'Emirati Arabi Uniti',
      DE: 'Vereinigte Arabische Emirate',
    },
  },
  UM: {
    code: 'UM',
    label: {
      EN: 'United States Minor Outlying Islands',
      FR: 'Îles Mineures Éloignées des États-Unis',
      IT: 'Isole minori esterne degli Stati Uniti',
      DE: 'Kleinere Inselbesitzungen der Vereinigten Staaten',
    },
  },
  UY: {
    code: 'UY',
    label: {
      EN: 'Uruguay',
      FR: 'Uruguay',
      IT: 'Uruguay',
      DE: 'Uruguay',
    },
  },
  UZ: {
    code: 'UZ',
    label: {
      EN: 'Uzbekistan',
      FR: 'Ouzbékistan',
      IT: 'Uzbekistan',
      DE: 'Usbekistan',
    },
  },
  VU: {
    code: 'VU',
    label: {
      EN: 'Vanuatu',
      FR: 'Vanuatu',
      IT: 'Vanuatu',
      DE: 'Vanuatu',
    },
  },
  VE: {
    code: 'VE',
    label: {
      EN: 'Venezuela',
      FR: 'Venezuela',
      IT: 'Venezuela',
      DE: 'Venezuela',
    },
  },
  VN: {
    code: 'VN',
    label: {
      EN: 'Viet Nam',
      FR: 'Viet Nam',
      IT: 'Vietnam',
      DE: 'Vietnam',
    },
  },
  VG: {
    code: 'VG',
    label: {
      EN: 'Virgin Islands, British',
      FR: 'Îles Vierges Britanniques',
      IT: 'Isole Vergini britanniche',
      DE: 'Britische Jungferninseln',
    },
  },
  VI: {
    code: 'VI',
    label: {
      EN: 'Virgin Islands, U.S.',
      FR: 'Îles Vierges Américaines',
      IT: 'Isole Vergini americane',
      DE: 'Amerikanische Jungferninseln',
    },
  },
  WF: {
    code: 'WF',
    label: {
      EN: 'Wallis and Futuna',
      FR: 'Wallis et Futuna',
      IT: 'Wallis e Futuna',
      DE: 'Wallis und Futuna',
    },
  },
  EH: {
    code: 'EH',
    label: {
      EN: 'Western Sahara',
      FR: 'Sahara Occidental',
      IT: 'Sahara Occidentale',
      DE: 'Westsahara',
    },
  },
  YE: {
    code: 'YE',
    label: {
      EN: 'Yemen',
      FR: 'Yémen',
      IT: 'Yemen',
      DE: 'Jemen',
    },
  },
  ZM: {
    code: 'ZM',
    label: {
      EN: 'Zambia',
      FR: 'Zambie',
      IT: 'Zambia',
      DE: 'Sambia',
    },
  },
  ZW: {
    code: 'ZW',
    label: {
      EN: 'Zimbabwe',
      FR: 'Zimbabwe',
      IT: 'Zimbabwe',
      DE: 'Simbabwe',
    },
  },
  '': {
    code: '',
    label: {
      EN: '_________',
      FR: '_________',
      IT: '_________',
      DE: '_________',
    },
  },
} as const

export const currenciesList = {
  EUR: {
    code: 'EUR',
    label: {
      EN: 'Euro',
      FR: 'Euro',
      IT: 'Euro',
      DE: 'Euro',
    },
    symbol: '€',
    flag: 'eu',
  },
  USD: {
    code: 'USD',
    label: {
      EN: 'United States Dollar',
      FR: 'Dollar américain',
      IT: 'Dollaro statunitense',
      DE: 'US-Dollar',
    },
    symbol: '$',
    flag: 'us',
  },
  GBP: {
    code: 'GBP',
    label: {
      EN: 'British Pound Sterling',
      FR: 'Livre Sterling britannique',
      IT: 'Sterlina britannica',
      DE: 'Britisches Pfund',
    },
    symbol: '£',
    flag: 'gb',
  },
  DKK: {
    code: 'DKK',
    label: {
      EN: 'Danish Krone',
      FR: 'Couronne Danoise',
      IT: 'Corona danese',
      DE: 'Dänische Krone',
    },
    symbol: 'kr',
    flag: 'dk',
  },
  SEK: {
    code: 'SEK',
    label: {
      EN: 'Swedish Krona',
      FR: 'Couronne Suédoise',
      IT: 'Corona svedese',
      DE: 'Schwedische Krone',
    },
    symbol: 'kr',
    flag: 'se',
  },
} as const
